import { useCallback, useId, useState } from 'react';

import { FASTSHIFT_ASSESSMENT_RISKS } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { EmptySearchVector } from '@/common/displays/assets/empty-search-vector';

import { AssessmentResultCard } from './assessment-result-card';
import {
	ASSESSMENT_RESULTS_STATUS_FILTER,
	AssessmentResultsStatusFilter,
} from './assessment-results-status-filter';
import { mapAssessmentResultCardViewModel } from './map-assessment-result-card-view-model';
import { messages } from './messages';

import type { AssessmentResultsStatusFilterValue } from './assessment-results-status-filter';
import type { FastshiftAssessmentResult } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

const styles = cssMap({
	section: {
		width: '100%',
	},
	headingGroup: {
		width: '100%',
	},
	toolbar: {
		width: '100%',
	},
	list: {
		marginBlockStart: token('space.0'),
		marginBlockEnd: token('space.0'),
		marginInlineStart: token('space.0'),
		marginInlineEnd: token('space.0'),
		paddingBlockStart: token('space.0'),
		paddingBlockEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		width: '100%',
	},
	loadingArea: {
		paddingBlockStart: token('space.800'),
		paddingBlockEnd: token('space.800'),
	},
	emptyState: {
		paddingBlockStart: token('space.400'),
		paddingBlockEnd: token('space.400'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
		textAlign: 'center',
	},
});

export type AssessmentResultsSectionProps = {
	isLoading: boolean;
	results: FastshiftAssessmentResult[];
	selectedResultId: string | null;
	onResultSelect: (result: FastshiftAssessmentResult) => void;
	onFilterChange?: (filter: AssessmentResultsStatusFilterValue | null) => void;
};

const isNeedsAttention = ({ impact }: FastshiftAssessmentResult) =>
	impact?.status === FASTSHIFT_ASSESSMENT_RISKS.MEDIUM_RISK ||
	impact?.status === FASTSHIFT_ASSESSMENT_RISKS.HIGH_RISK;

const isPassed = ({ impact }: FastshiftAssessmentResult) =>
	impact?.status === FASTSHIFT_ASSESSMENT_RISKS.LOW_RISK;

const isSkipped = ({ impact }: FastshiftAssessmentResult) =>
	impact?.status === FASTSHIFT_ASSESSMENT_RISKS.SKIPPED;

const filterPredicates: Record<
	AssessmentResultsStatusFilterValue,
	(result: FastshiftAssessmentResult) => boolean
> = {
	[ASSESSMENT_RESULTS_STATUS_FILTER.PASSED]: isPassed,
	[ASSESSMENT_RESULTS_STATUS_FILTER.NEEDS_ATTENTION]: isNeedsAttention,
	[ASSESSMENT_RESULTS_STATUS_FILTER.SKIPPED]: isSkipped,
};

export const AssessmentResultsSection = ({
	isLoading,
	results,
	selectedResultId,
	onResultSelect,
	onFilterChange,
}: AssessmentResultsSectionProps) => {
	const { formatMessage, formatNumber } = useIntl();
	const [filter, setFilter] = useState<AssessmentResultsStatusFilterValue | null>(null);
	const sectionHeadingId = useId();
	const namedResults = results.filter(({ dataPoint }) => dataPoint?.trim());
	const visibleResults = filter ? namedResults.filter(filterPredicates[filter]) : namedResults;
	const handleFilterChange = useCallback(
		(nextFilter: AssessmentResultsStatusFilterValue | null) => {
			setFilter(nextFilter);
			onFilterChange?.(nextFilter);
		},
		[onFilterChange],
	);

	return (
		<Box as="section" aria-labelledby={sectionHeadingId} xcss={styles.section}>
			<Stack space="space.200">
				<Stack space="space.050" xcss={styles.headingGroup}>
					<Heading id={sectionHeadingId} size="small" as="h2">
						{formatMessage(messages.sectionTitle)}
					</Heading>
					<Text as="p">{formatMessage(messages.sectionDescription)}</Text>
				</Stack>

				{isLoading ? (
					<Stack space="space.200">
						<Inline spread="space-between" alignBlock="center" xcss={styles.toolbar}>
							<Skeleton
								width={token('space.1000')}
								height={token('space.500')}
								isShimmering
								testId="assessment-results-filter-skeleton"
							/>
							<Skeleton
								width={token('space.1000')}
								height={token('space.250')}
								isShimmering
								testId="assessment-results-count-skeleton"
							/>
						</Inline>
						<Stack alignInline="center" xcss={styles.loadingArea}>
							<Spinner
								size="large"
								label={formatMessage(messages.loadingResults)}
								interactionName="load-assessment-results"
								testId="assessment-results-spinner"
							/>
						</Stack>
					</Stack>
				) : (
					<Stack space="space.200">
						<Inline
							spread="space-between"
							alignBlock="center"
							shouldWrap
							space="space.100"
							xcss={styles.toolbar}
						>
							<AssessmentResultsStatusFilter value={filter} onChange={handleFilterChange} />
							<Text as="p">
								{formatMessage(messages.resultCount, {
									visibleCount: visibleResults.length,
									totalCount: namedResults.length,
								})}
							</Text>
						</Inline>

						{visibleResults.length > 0 ? (
							<Stack as="ul" space="space.100" xcss={styles.list}>
								{visibleResults.map((result) => (
									<AssessmentResultCard
										key={result.id}
										{...mapAssessmentResultCardViewModel(result, formatNumber)}
										isSelected={result.id === selectedResultId}
										onSelect={() => onResultSelect(result)}
									/>
								))}
							</Stack>
						) : (
							<Box xcss={styles.emptyState}>
								<Stack alignBlock="center" alignInline="center" space="space.100">
									<EmptySearchVector />
									<Text>
										{formatMessage(
											namedResults.length === 0
												? messages.emptyResults
												: messages.noMatchingResults,
										)}
									</Text>
								</Stack>
							</Box>
						)}
					</Stack>
				)}
			</Stack>
		</Box>
	);
};
